import { useTranslation } from 'react-i18next';
import { useParams } from '@tanstack/react-router';
import { MediaPreviewWithMetrics } from '@anytag/shared-atoms';
import { LIMIT_30, THEME } from '@anytag/shared-constants';
import { bigIntFormatter } from '@anytag/shared-utils';
import { usePostDetailsParam, useQueryHelper } from '@anytag/shared-hooks';
import { TwitterAnalyticsPostItemPayload, useTwitterAnalyticsPostsQuery } from '@anytag/graphql';
import { ImageSlider, InfluencerTwitterAnalyticsPostDetailsModal } from '@anytag/shared-molecules';
import { PostDetailsType } from '@anytag/shared-types';
import { AnalyticsFeedPostsWrapper, AnalyticsFeedPostsWrapperProps } from '../../shared';
import { AnalyticsSelectedAccountInfo } from '../../types';
import { useAnalyticsProfileSearch } from '../../hooks';

export interface AnalyticsTwitterDashboardFeedPostsTemplateProps
  extends Pick<AnalyticsFeedPostsWrapperProps, 'title' | 'loading' | 'moreDetailProps'> {
  data?: readonly TwitterAnalyticsPostItemPayload[];
}

export const AnalyticsTwitterDashboardFeedPostsTemplate = ({
  data = [],
  ...restProps
}: AnalyticsTwitterDashboardFeedPostsTemplateProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  return (
    <AnalyticsFeedPostsWrapper dataNotAvailable={!data.length} title={t('HeaderColumn.Feed Posts')} {...restProps}>
      <ImageSlider total={data.length} css={{ paddingLeft: '16px' }}>
        {data.map(({ id, thumbnail, like, reply, title, retweet }) => (
          <MediaPreviewWithMetrics
            key={id}
            title={title}
            src={thumbnail}
            onClick={() => setPostDetailsParam(id)}
            css={THEME.mediaPreview.hasCaptionOrTitle}
            statistics={[
              { label: t('General.Like'), value: bigIntFormatter(like, 0, { k: true, m: true }) },
              { label: t('General.Reply'), value: bigIntFormatter(reply, 0, { k: true, m: true }) },
              { label: t('General.Retweet'), value: bigIntFormatter(retweet, 0, { k: true, m: true }) }
            ]}
          />
        ))}
      </ImageSlider>
    </AnalyticsFeedPostsWrapper>
  );
};

export interface AnalyticsTwitterDashboardFeedPostsProps {
  brandAccountName?: string;
  analyticsAccount: AnalyticsSelectedAccountInfo;
}

export const AnalyticsTwitterDashboardFeedPosts = ({
  analyticsAccount,
  brandAccountName
}: AnalyticsTwitterDashboardFeedPostsProps) => {
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { filter } = useAnalyticsProfileSearch();
  const params = useParams({
    from: '/_private-routes/analytics/$brandAccountId/$socialMedia/$socialMediaAccountId/dashboard/'
  });
  const { data, loading } = useTwitterAnalyticsPostsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      offset: 0,
      limit: LIMIT_30,
      endDate: filter.endDate,
      startDate: filter.startDate,
      orderBy: { field: null, order: null },
      twitterAnalyticsAccountId: analyticsAccount.id
    },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      navigate({ to: '/analytics' });
    }
  });

  const posts = data?.twitterAnalyticsPosts.posts || [];

  return (
    <>
      <AnalyticsTwitterDashboardFeedPostsTemplate
        data={posts}
        loading={loading}
        moreDetailProps={{
          to: '/analytics/$brandAccountId/$socialMedia/$socialMediaAccountId/dashboard/twitter_feed_posts',
          params,
          search: {
            filter: {
              brandAccountName,
              endDate: filter.endDate,
              startDate: filter.startDate
            }
          }
        }}
      />

      <InfluencerTwitterAnalyticsPostDetailsModal
        isAnalytics
        posts={posts.map(({ id }): PostDetailsType => ({ id, mainSocialAccountId: analyticsAccount.id }))}
      />
    </>
  );
};
